import IconHorizontalRebrand from '@/assets/logos/icon-horizontal-svg/icon-horizontal-rebrand.svg';
import IconRoundBarkBgWhite from '@/assets/logos/icon-round-bark-bg-svg/icon-round-bark-bg-white.svg';
import { PhoneInput } from '@/components/ui/phone-input';
import { type useMeetingBooker } from '@/container/demo-flow/meeting-booker/hooks/useMeetingBooker';
import { MeetingCalendarPanel } from '@/container/demo-flow/meeting-booker/meeting-booker-steps';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { SunlightPatternBackground } from '@heidi/growth';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { Loader } from '@heidi/snowflake-ui/loader';
import { type CountryType } from '@heidi/utils/countries';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import Image from 'next/image';
import { FormattedMessage, useIntl } from 'react-intl';
import { type Country } from 'react-phone-number-input';

import { ChoiceCard } from './choice-card';
import { DemoCountryLanguageFields } from './demo-country-language-fields';

type MeetingBooker = ReturnType<typeof useMeetingBooker>;

interface BookDemoMeetingVariantProps {
  email: string;
  country: CountryType | null;
  onCountryChange: (country: CountryType) => void;
  languageCode: string | null;
  onLanguageChange: (language: string) => void;
  showLanguage: boolean;
  availableLanguages: LanguageCode[];
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  showPhoneNumber: boolean;
  isPhoneNumberOptional: boolean;
  hasCountryPhoneMismatch: boolean;
  phoneRequiredButMissing: boolean;
  meeting: MeetingBooker;
  meetingUrl: string | null;
  selectedDateTimeLabel: string | null | undefined;
  bookingError: string | undefined;
  onSelectSlot: Parameters<typeof MeetingCalendarPanel>[0]['onSelectSlot'];
  onContinue: () => void;
  onBack: () => void;
  onSignup: () => void;
}

export function BookDemoMeetingVariant({
  email,
  country,
  onCountryChange,
  languageCode,
  onLanguageChange,
  showLanguage,
  availableLanguages,
  phoneNumber,
  onPhoneNumberChange,
  showPhoneNumber,
  isPhoneNumberOptional,
  hasCountryPhoneMismatch,
  phoneRequiredButMissing,
  meeting,
  meetingUrl,
  selectedDateTimeLabel,
  bookingError,
  onSelectSlot,
  onContinue,
  onBack,
  onSignup,
}: BookDemoMeetingVariantProps) {
  const intl = useIntl();

  return (
    <div className="relative z-10 flex w-full flex-col items-center gap-10 px-6 py-8">
      <div className="flex flex-col items-center gap-6">
        <Image
          src={IconHorizontalRebrand}
          alt="Heidi"
          width={80}
          height={24}
          className="dark-invert"
        />
        <div className="flex flex-col items-center gap-1 text-center">
          <TypographyH2>
            <FormattedMessage id="6+NbDkdsvI" defaultMessage="Book a demo" />
          </TypographyH2>
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage
              id="uycOOBgyOv"
              defaultMessage="Book a demo with our team"
            />
          </TypographyP2>
        </div>
      </div>

      <div className="flex w-full max-w-[560px] flex-col gap-4 rounded-2xl bg-surface p-6 shadow-lg">
        <DemoCountryLanguageFields
          country={country}
          onCountryChange={onCountryChange}
          languageCode={languageCode}
          onLanguageChange={onLanguageChange}
          showLanguage={showLanguage}
          availableLanguages={availableLanguages}
        />

        {showPhoneNumber && meetingUrl && (
          <div className="flex flex-col gap-1">
            {isPhoneNumberOptional ? (
              <TypographyP2Bold className="leading-tight">
                <FormattedMessage
                  id="JNTB42CB9b"
                  defaultMessage="Phone number (optional)"
                />
              </TypographyP2Bold>
            ) : (
              <div className="flex gap-0.5">
                <TypographyP2Bold className="leading-tight">
                  <FormattedMessage id="jdJhOLwFfi" defaultMessage="Phone number" />
                </TypographyP2Bold>
                <TypographyP2Bold className="text-validation-destructive">
                  *
                </TypographyP2Bold>
              </div>
            )}
            <PhoneInput
              value={phoneNumber}
              onChange={(value) => onPhoneNumberChange((value as string) ?? '')}
              defaultCountry={country?.countryCode as Country | undefined}
              placeholder={intl.formatMessage({
                id: '7eGP2uVRxf',
                defaultMessage: '+1 (555) 123-4567',
              })}
              data-testid="demo-phone-input"
            />
            {hasCountryPhoneMismatch && (
              <div className="flex items-start gap-2 rounded-md bg-validation-warning-muted p-2">
                <AlertCircle className="mt-0.5 size-4 shrink-0 text-validation-warning" />
                <TypographyCaption className="text-validation-warning">
                  <FormattedMessage
                    id="IHq/dry2fB"
                    defaultMessage="This phone number doesn’t match the selected country."
                  />
                </TypographyCaption>
              </div>
            )}
          </div>
        )}

        {!meetingUrl && country && (
          <div className="flex flex-col gap-3">
            <TypographyP2Bold>
              <FormattedMessage
                id="IjiTD8QTFy"
                defaultMessage="Demos are not available in your language / region"
              />
            </TypographyP2Bold>
            <ChoiceCard
              title={intl.formatMessage({
                id: 'cPh9OCFhsM',
                defaultMessage: 'Sign up free',
              })}
              description={intl.formatMessage({
                id: 'TpStGaZtU0',
                defaultMessage: 'Get started in seconds',
              })}
              data-testid="fallback-signup"
              icon={
                <div className="relative flex size-full items-center justify-center">
                  <SunlightPatternBackground />
                  <div className="relative z-10 rounded-full border border-bark-900/20 p-1.5">
                    <div className="rounded-full border border-bark-900/30 p-1">
                      <Image src={IconRoundBarkBgWhite} alt="" width={44} height={44} />
                    </div>
                  </div>
                </div>
              }
              onClick={onSignup}
            />
            <Button
              size="lg"
              variant="link"
              onClick={() => {
                window.location.href = 'https://www.heidihealth.com';
              }}
              className="text-text-secondary"
            >
              <FormattedMessage id="pyJuhkhg9l" defaultMessage="Or return to Heidi" />
            </Button>
          </div>
        )}

        {meetingUrl && (
          <TypographyP2Bold data-testid="selected-datetime">
            {selectedDateTimeLabel ?? (
              <FormattedMessage id="JJhyw5bphp" defaultMessage="Select a time" />
            )}
          </TypographyP2Bold>
        )}

        {meetingUrl && (
          <div className="flex w-full justify-center">
            <MeetingCalendarPanel
              isLoading={meeting.isLoadingAvailability}
              availableDates={meeting.availableDates}
              selectedDate={meeting.selectedDate}
              onSelectDate={meeting.handleSelectDate}
              activeDuration={meeting.activeDuration}
              onSelectDuration={meeting.handleSelectDuration}
              slots={meeting.slotsForSelectedDate}
              timezone={meeting.timezone}
              onSelectSlot={onSelectSlot}
            />
          </div>
        )}

        {(bookingError || meeting.bookingError) && (
          <TypographyP2 className="text-center text-validation-destructive">
            {bookingError || meeting.bookingError}
          </TypographyP2>
        )}

        {meetingUrl && (
          <Button
            size="lg"
            disabled={
              !meeting.selectedSlot ||
              !email.trim() ||
              meeting.isBooking ||
              phoneRequiredButMissing
            }
            onClick={onContinue}
            data-testid="demo-book-button"
          >
            {meeting.isBooking ? (
              <Loader className="size-5 text-text-primary-invert" />
            ) : (
              <FormattedMessage
                id="cIBHfEmk8t"
                defaultMessage="Continue"
                description="Primary action to proceed with booking a demo"
              />
            )}
          </Button>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="gap-1.5 text-text-secondary"
      >
        <ArrowLeft size={16} className="rtl:-scale-x-100" />
        <FormattedMessage id="cyR7KhiuaU" defaultMessage="Back" />
      </Button>
    </div>
  );
}
